/**
 * What a payment or deposit actually took off the customer.
 *
 * Every money row in this app stores the NET: `Payment.amount` is what the
 * invoice was credited, `Deposit.amount` is what the credit is worth. A card
 * SURCHARGE is captured on top of that and recorded beside it, so any surface
 * rendering the net alone under-reports what left the customer's card.
 *
 * Three traps this exists to hold in one place:
 *
 *  - **The fee is a string.** `surchargeAmount` is a Prisma `Decimal(14,2)` and
 *    the payments routes have no serializer in front of it, so it reaches the
 *    browser as `"30.00"`. `amount + surchargeAmount` unguarded yields
 *    `"100030.00"`.
 *  - **A tip is already inside `amount`.** The NOT-535 inline-tip split records
 *    the gratuity in `gatewayMetadata.tipCents` while `amount` stays
 *    `base + tip`, so the charged total is `amount + fee` and nothing more.
 *    Adding the tip again double-counts it. This matches the canonical receipt
 *    formula (`receipts.service.ts`: `totalAmount = paymentAmount + surcharge`).
 *  - **A reversed fee is zero.** When a card turns out to be debit the fee is
 *    refunded and `surchargeAmount` is zeroed, so reading the stored value —
 *    rather than recomputing from tenant settings — is what keeps the display
 *    honest about money that went back.
 */
export function cardServiceFee(source: {
  surchargeAmount?: number | string | null;
  serviceFeeAmount?: number | string | null;
}): number {
  const raw = source.surchargeAmount ?? source.serviceFeeAmount ?? 0;
  const fee = Number(raw);
  return Number.isFinite(fee) && fee > 0 ? fee : 0;
}

/**
 * The card service fee that has already gone back to the customer with
 * refunds. Refunds return the fee pro-rata with the principal; the running
 * total is booked on the payment as `gatewayMetadata.surchargeRefundedCents`
 * while `surchargeAmount` stays what was charged (just as `amount` stays the
 * net and `refundedAmount` carries what went back).
 */
export function cardServiceFeeReturned(source: {
  gatewayMetadata?: Record<string, unknown> | null;
}): number {
  const raw = Number(source.gatewayMetadata?.['surchargeRefundedCents'] ?? 0);
  return Number.isFinite(raw) && raw > 0 ? raw / 100 : 0;
}

/**
 * What the PROCESSOR keeps out of the money we receive — the opposite
 * direction to `cardServiceFee`, which is charged on top of it.
 *
 * **This figure is our own estimate, never the processor's reported cut.**
 * `processingFeeCents` is written at charge time from the tenant's configured
 * fee schedule, falling back to the provider's default rates (Payabli: 2.5% +
 * $0.25 card, 0.5% + $0.25 ACH) when no schedule is configured — which is the
 * ordinary case. Payabli does return a real `feeAmount`/`netAmount` on the
 * charge response, but nothing reads them back, so a payout can never be
 * reconciled against this number. Every surface that renders it must say so.
 *
 * `null` — not `0` — when the column is absent (a manual cash payment, or a
 * role whose field mask withholds it), so a missing value renders as "—"
 * instead of claiming the processor took nothing.
 */
export function estimatedProcessingFee(source: {
  processingFeeCents?: number | string | null;
  status?: string | null;
}): number | null {
  // A failed payment (a staff-cancelled bank debit included) moved no money,
  // so the processor kept no fee; the estimate stored at charge time is moot.
  if (String(source.status ?? '').toUpperCase() === 'FAILED') return null;
  const raw = source.processingFeeCents;
  if (raw == null || raw === '') return null;
  const cents = Number(raw);
  return Number.isFinite(cents) && cents >= 0 ? cents / 100 : null;
}

/** The net plus the card service fee — the figure that reconciles against the processor. */
export function collectedTotal(source: {
  amount?: number | string | null;
  surchargeAmount?: number | string | null;
  serviceFeeAmount?: number | string | null;
}): number {
  const net = Number(source.amount ?? 0);
  return (Number.isFinite(net) ? net : 0) + cardServiceFee(source);
}

/**
 * What the business keeps from a payment after refunds: its net at capture
 * minus the principal refunded since.
 *
 * `netAmountCents` is booked at capture as `amount − processing fee + card
 * service fee`, and a refund only takes the returned SERVICE FEE back out of it
 * — the principal refunded never touches it. Reading it alone showed a fully
 * refunded $100 card payment (fee $3.20) as Net $96.80, while the same refund
 * on a cash payment (no `netAmountCents`, so `amount − refunded`) showed $0.00.
 * One rule for both: start from what capture left us, take the refunds off. A
 * full refund of a card payment therefore goes negative by the processing fee,
 * which the processor keeps.
 */
export function netAfterRefunds(source: {
  amount?: number | string | null;
  refundedAmount?: number | string | null;
  netAmountCents?: number | string | null;
}): number {
  const captureCents = Number(source.netAmountCents);
  const atCapture =
    source.netAmountCents != null && source.netAmountCents !== '' && Number.isFinite(captureCents)
      ? captureCents / 100
      : Number(source.amount ?? 0);
  const refunded = Number(source.refundedAmount ?? 0);
  const net =
    (Number.isFinite(atCapture) ? atCapture : 0) - (Number.isFinite(refunded) ? refunded : 0);
  return Math.round(net * 100) / 100;
}
